import React, { useEffect, useRef, useState } from "react";
import manifest from "../data/landingTournament.json";
import FloatingButton from "./FloatingButton.jsx";
import { getLandingAssignment, getLandingContent, persistLandingCookie, retryLandingEvents, trackLandingEvent } from "../utils/landingTournament.js";
import styles from "./LandingExperiment.module.css";
import firstFace from "../img/onb-page/first-face-onb.svg";
import secondFace from "../img/onb-page/second-face-onb.svg";
import thirdFace from "../img/onb-page/third-face-onb.svg";

const situations = [
  ["돈을 준다더니 계속 미뤄요", "누가 언제 주기로 했는지, 지금까지 어떤 말을 했는지 적어주세요."],
  ["일은 했는데 월급이 안 들어와요", "언제부터 일했고, 얼마를 못 받았는지부터 이야기해 주세요."],
  ["다른 사람들 앞에서 심한 말을 들었어요", "어디서 어떤 말을 들었는지, 그 자리에 누가 있었는지 알려주세요."],
  ["계약할 때 들은 얘기와 달라요", "처음 약속과 실제로 달라진 점을 적어주세요. 어려운 말로 바꿀 필요 없어요."],
];

function Steps() {
  return <ol data-behavior="landing-steps" className={styles.steps}>
    <li><span>01</span><h3>일단 있었던 일부터</h3><p>누구와 무슨 일이 있었는지 적어주세요. 순서가 좀 뒤섞여도 괜찮아요.</p></li>
    <li><span>02</span><h3>빠진 얘기는 대화하면서</h3><p>로우로우가 물어보면 답해주세요. 판례를 찾는 데 필요한 내용을 정리해요.</p></li>
    <li><span>03</span><h3>비슷한 사건의 판결까지</h3><p>로그인하고 판례를 찾아보세요. 법원이 어떤 사실을 중요하게 봤는지 읽을 수 있어요.</p></li>
  </ol>;
}

function Preview() {
  return <div data-behavior="landing-preview" className={styles.preview}>
    <blockquote>“친구한테 돈을 빌려줬는데,<br />갚기로 한 날이 지나도 안 줘요.”</blockquote>
    <p className={styles.caption}>이런 상황을 적었다고 해볼게요. 아래는 이해를 돕기 위한 예시예요.</p>
    <div className={styles.comparison}>
      <div><h3>먼저 이런 내용을 정리해요</h3><p>돈을 보낸 기록이 있는지,<br />언제 갚기로 했는지 물어봐요.</p></div>
      <div><h3>판례에선 이런 걸 살펴봐요</h3><p>돈을 빌려준 사실을 어떻게 확인했는지,<br />어떤 기록이 판단에 영향을 줬는지 읽어봐요.</p></div>
    </div>
  </div>;
}

function Conditions() {
  return <dl data-behavior="landing-conditions" className={styles.conditions}>
    <div><dt>대화는 무료로</dt><dd>로그인 없이 대화 한 번은 무료예요.</dd></div>
    <div><dt>판례는 로그인 후에</dt><dd>로그인하면 내 로우와 판례 검색에 필요한 이용 조건을 확인할 수 있어요.</dd></div>
    <div><dt>로우가 드는 기능은</dt><dd>누르기 전에 필요한 로우를 확인하고 선택하세요.</dd></div>
  </dl>;
}

export default function LandingExperiment({ children, scrollRef, onLogin }) {
  const [assignment] = useState(() => getLandingAssignment());
  const marker = useRef(null);
  const candidate = getLandingContent(assignment.variantId, manifest);

  useEffect(() => {
    persistLandingCookie(assignment);
    void trackLandingEvent("assigned", assignment);
    const retry = () => { if (document.visibilityState === "visible") void retryLandingEvents(assignment); };
    const interval = assignment.measurable && !assignment.internal ? window.setInterval(retry, 10000) : undefined;
    window.addEventListener("online", retry);
    let timer;
    const observer = marker.current && typeof IntersectionObserver !== "undefined" ? new IntersectionObserver(([entry]) => {
      window.clearTimeout(timer);
      if (entry.isIntersecting) timer = window.setTimeout(() => {
        if (document.visibilityState === "visible") void trackLandingEvent("exposed", assignment);
      }, 1000);
    }) : undefined;
    if (observer) observer.observe(marker.current);
    return () => { observer?.disconnect(); window.clearTimeout(timer); window.clearInterval(interval); window.removeEventListener("online", retry); };
  }, [assignment]);

  const begin = () => {
    void trackLandingEvent("cta", assignment);
    onLogin();
  };

  return <>
    <FloatingButton inline scrollToRef={scrollRef} label={candidate?.floatingLabel} />
    <div id="lawlow-landing" data-behavior="landing-candidate" data-landing-variant={candidate?.id || "main"} className={styles.boundary}>
    <span ref={marker} className={styles.marker} aria-hidden="true" />
    {candidate ? <section ref={scrollRef} className={styles.landing} aria-label="로우로우 서비스 안내">
      <div className={styles.inner}>
        <header data-behavior="landing-heading" className={styles.heading}>
          <p className={styles.eyebrow}>{candidate.eyebrow}</p>
          <h2>{candidate.title}</h2>
          <p className={styles.description}>{candidate.description}</p>
        </header>
        {candidate.layout === "problem" && <><div className={styles.questionCards}>
          <div><img src={firstFace} alt="" /><p>돈 달라는 말인데,<br /><strong>왜 내가 눈치를 봐야 하죠?</strong></p></div>
          <div><img src={secondFace} alt="" /><p>분명 억울한데,<br /><strong>뭐가 잘못인지 설명이 안 돼요.</strong></p></div>
          <div><img src={thirdFace} alt="" /><p>찾아보면 말이 다 달라요.<br /><strong>법원은 어떻게 판단했을까요?</strong></p></div>
        </div><Steps /></>}
        {candidate.layout === "preview" && <Preview />}
        {candidate.layout === "situations" && <div className={styles.situations}>
          {situations.map(([title, text], index) => <details data-behavior={`landing-situation-${index+1}`} key={title}>
            <summary><span>0{index + 1}</span>{title}<b aria-hidden="true">＋</b></summary><p>{text}</p>
          </details>)}
        </div>}
        {candidate.layout === "steps" && <><blockquote className={styles.simpleQuote}>“어디서부터 설명해야 할지 모르겠는데요.”<strong>지금 제일 신경 쓰이는 일부터 적어주세요.</strong></blockquote><Steps /></>}
        <Conditions />
        <div className={styles.action}>
          <button data-behavior="landing-login" type="button" onClick={begin}>로그인하고 판례 찾기 <span aria-hidden="true">↗</span></button>
          <p>카카오 · 네이버 · Google · Apple 계정으로 로그인</p>
        </div>
        <p className={styles.note}>판례는 상황을 이해하는 참고 자료입니다. 구체적인 사실에 따라 판단은 달라질 수 있어요.</p>
      </div>
    </section> : children}
  </div></>;
}
